export interface FilterValue {
	value: string;
	label: string;
	count?: number;
	selected: boolean;
}

interface FilterGroupBase {
	id: string;
	/** A group with no label shows under the group above it, or alone at the top. */
	label?: string;
}

export interface ListFilterGroup extends FilterGroupBase {
	kind: "list";
	values: FilterValue[];
	maxShown?: number;
}

export interface ToggleFilterGroup extends FilterGroupBase {
	kind: "toggle";
	value: FilterValue;
}

export interface FilterRange {
	min?: number;
	max?: number;
}

export interface RangeFilterGroup extends FilterGroupBase {
	kind: "range";
	bounds: { min: number; max: number };
	selected: FilterRange;
	locale?: string;
	formatOptions?: Intl.NumberFormatOptions;
}

export type FilterGroup =
	ListFilterGroup | ToggleFilterGroup | RangeFilterGroup;

export interface FilterValueRef {
	groupId: string;
	value: string;
}

export interface FilterValueChange extends FilterValueRef {
	selected: boolean;
}

export interface FilterRangeChange extends FilterRange {
	groupId: string;
}

export interface AppliedFilter extends FilterValueRef {
	label: string;
}

export interface SortOption {
	value: string;
	label: string;
}

export type FilterPanelPresentation = "inline" | "drawer";

export const RANGE_VALUE = "range";

export function isFilterGroupActive(group: FilterGroup): boolean {
	switch (group.kind) {
		case "list":
			return group.values.length > 0;
		case "toggle":
			return true;
		case "range":
			return (
				group.bounds.max > group.bounds.min || hasRange(group.selected)
			);
	}
}

export function selectedCount(group: FilterGroup): number {
	switch (group.kind) {
		case "list":
			return group.values.filter((value) => value.selected).length;
		case "toggle":
			return group.value.selected ? 1 : 0;
		case "range":
			return hasRange(group.selected) ? 1 : 0;
	}
}

function hasRange(range: FilterRange): boolean {
	return range.min !== undefined || range.max !== undefined;
}

export function formatRange(
	range: FilterRange,
	locale = "en-US",
	formatOptions?: Intl.NumberFormatOptions
): string {
	const format = new Intl.NumberFormat(locale, formatOptions);
	if (range.min !== undefined && range.max !== undefined) {
		return `${format.format(range.min)} - ${format.format(range.max)}`;
	}
	if (range.min !== undefined) return `${format.format(range.min)} & Up`;
	if (range.max !== undefined) return `Up to ${format.format(range.max)}`;
	return "";
}

export function selectAppliedFilters(groups: FilterGroup[]): AppliedFilter[] {
	return groups.flatMap((group): AppliedFilter[] => {
		switch (group.kind) {
			case "list":
				return group.values
					.filter((value) => value.selected)
					.map((value) => ({
						groupId: group.id,
						value: value.value,
						label: value.label
					}));
			case "toggle":
				return group.value.selected
					? [
							{
								groupId: group.id,
								value: group.value.value,
								label: group.value.label
							}
						]
					: [];
			case "range":
				return hasRange(group.selected)
					? [
							{
								groupId: group.id,
								value: RANGE_VALUE,
								label: formatRange(
									group.selected,
									group.locale,
									group.formatOptions
								)
							}
						]
					: [];
		}
	});
}
